import type { ReserveSnapshot } from './types';

/**
 * Display metadata per ticker. Unknown tickers (stocks listed later) fall back to a generated monogram and sort last.
 * `en` is the plain company name: no share class, listing form or other bracketed qualifier, the ticker already says it.
 * `shares` is the number of tokens that make up the company: shares outstanding in billions, for the SK hynix ADS the
 * ADS-equivalent count (711M ordinary shares x 10 ADS each), because the token tracks the ADS price. Figures from
 * stockanalysis.com / Yahoo Finance as of 2026-09-21; they move slowly (buybacks, issuance), so refresh them now and
 * then. Market cap = shares x live oracle price, which is what the default list order follows.
 */
interface Meta { en: string; hue: number; shares?: number }
const META: Record<string, Meta> = {
  TSLA: { en: 'Tesla', hue: 354, shares: 3.95 },
  MSFT: { en: 'Microsoft', hue: 205, shares: 7.43 },
  AAPL: { en: 'Apple', hue: 220, shares: 14.59 },
  AMZN: { en: 'Amazon', hue: 32, shares: 10.79 },
  META: { en: 'Meta Platforms', hue: 215, shares: 2.55 },
  GOOGL: { en: 'Alphabet', hue: 45, shares: 12.23 },
  NVDA: { en: 'NVIDIA', hue: 90, shares: 24.15 },
  SPCX: { en: 'SpaceX', hue: 235, shares: 13.57 },
  MU: { en: 'Micron Technology', hue: 195, shares: 1.13 },
  SNDK: { en: 'Sandisk', hue: 8, shares: 0.146 },
  SKHY: { en: 'SK Hynix', hue: 18, shares: 7.11 },
  AMD: { en: 'AMD', hue: 160, shares: 1.63 },
  INTC: { en: 'Intel', hue: 210, shares: 5.25 },
  USDG: { en: 'Global Dollar', hue: 200 },
  ETH: { en: 'Ether', hue: 250 },
};

export function assetMeta(symbol: string): Meta {
  const known = META[symbol];
  if (known) return known;
  let h = 0;
  for (const c of symbol) h = (h * 31 + c.charCodeAt(0)) % 360;
  return { en: symbol, hue: h };
}

/** Market capitalisation in USD implied by the oracle price (8 decimals); null for a stock without a share count. */
export function marketCapUsd(symbol: string, priceUsd: string | bigint): number | null {
  const shares = assetMeta(symbol).shares;
  if (shares === undefined) return null;
  return shares * 1e9 * (Number(priceUsd) / 1e8);
}

/**
 * The canonical list order of the market: the borrowable stablecoin first, then the stocks by market cap (largest
 * first), then anything without a known share count, alphabetically. Used for the market snapshot itself, so every
 * list in the app (dashboard, faucet, modals) shows the same order.
 */
export function compareReserves(a: Pick<ReserveSnapshot, 'symbol' | 'role' | 'priceUsd'>, b: Pick<ReserveSnapshot, 'symbol' | 'role' | 'priceUsd'>): number {
  if (a.role !== b.role) return a.role === 'borrow' ? -1 : 1;
  const ca = marketCapUsd(a.symbol, a.priceUsd), cb = marketCapUsd(b.symbol, b.priceUsd);
  if (ca !== null && cb !== null && ca !== cb) return cb - ca;
  if ((ca === null) !== (cb === null)) return ca === null ? 1 : -1;
  return a.symbol.localeCompare(b.symbol);
}
